import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'utils/dayjs';

import { fetchConvertorRatesGraph } from 'actions';
import { DEFAULT_MOBILE_DATE_FORMAT, TYPES_ENUM } from 'utils';
import {
  getConvertorRatesGraphIsFetchingForHooks,
  getConvertorRatesGraphIsInitializedForHooks,
  getConvertorRatesMatrix,
  getConvertorRatesGraphTimeLine,
  getCurrenciesPairInfoForHooks,
} from 'selectors';
import { useSelectorProps } from 'hooks';

import ContainerDimensions from 'components/ContainerDimensions';
import VirtualizedMultipleLineChart from 'components/VirtualizedMultipleLineChart';
import ButtonGroup from 'components/ButtonGroup';
import TermsFilters from './TermsFilters';
import FilterLabel from './FilterLabel';

import { FiltersWrapper, ButtonGroupWrapper } from './styles';

const terms = [
  { type: 'circle', color: 'secondary', value: 'banks' },
  { type: 'triangle', color: 'primary', value: 'mb' },
  { type: 'rect', color: 'yellow', value: 'auction' },
  { type: 'rhomb', color: 'blue', value: 'nbu' },
];

const periods = ['6', '29', '364', '1826'];

const DynamicRatesChart = () => {
  const dispatch = useDispatch();
  const { formatMessage, locale } = useIntl();

  const [period, changePeriod] = useState('6');
  const [filters, changeFilters] = useState({
    banks: true,
    mb: true,
    auction: true,
    nbu: true,
  });

  const startDate = useMemo(
    () =>
      dayjs()
        .subtract(parseInt(period, 10), 'days')
        .format(DEFAULT_MOBILE_DATE_FORMAT),
    [period]
  );

  const endDate = dayjs().format(DEFAULT_MOBILE_DATE_FORMAT);

  const selectorProps = useSelectorProps();

  const { source, target } = useSelector(state =>
    getCurrenciesPairInfoForHooks(state, selectorProps)
  );

  const params = useMemo(
    () => ({
      sourceCurrency: source.slug,
      targetCurrency: target.slug,
      period,
      startDate,
      endDate,
      locale,
    }),
    [source, target, period, startDate, endDate, period, locale]
  );

  useEffect(() => {
    if (source.slug && target.slug) {
      dispatch(fetchConvertorRatesGraph(params));
    }
  }, [params]);

  const isFetching = useSelector(state =>
    getConvertorRatesGraphIsFetchingForHooks(state, params)
  );
  const isInitialized = useSelector(state =>
    getConvertorRatesGraphIsInitializedForHooks(state, params)
  );
  const ratesMatrix = useSelector(state =>
    getConvertorRatesMatrix(state, params)
  );
  const ratesGraphTimeLine = useSelector(state =>
    getConvertorRatesGraphTimeLine(state, params)
  );

  const preparedTerms = useMemo(
    () =>
      terms.map(term => ({
        ...term,
        name: formatMessage({ id: `dynamicRates.filter.${term.value}` }),
        disabled: ratesMatrix[TYPES_ENUM[term.value]].length === 0,
      })),
    [ratesMatrix]
  );

  const preparedPeriods = useMemo(
    () =>
      periods.map(option => ({
        value: option,
        label: formatMessage({ id: 'periods' }, { period: option }),
      })),
    []
  );

  const activeTerms = useMemo(
    () => terms.filter(({ value }) => filters[value]),
    [filters]
  );

  const activeRates = useMemo(
    () => ratesMatrix.filter((_, index) => filters[terms[index].value]),
    [filters, ratesMatrix]
  );

  const ratesExtent = useMemo(() => {
    const allActiveRates = activeRates.flat();
    const allRates =
      allActiveRates.length > 0 ? allActiveRates : ratesMatrix.flat();

    const min = Math.floor(Math.min(...allRates));
    const max = Math.ceil(Math.max(...allRates));

    return [min, max];
  }, [activeRates]);

  const dotsCount = useMemo(() => {
    // Chart scale relies on `dotsCount` matching the number of points.
    // When backend returns different point counts per interval, prefer timeline length.
    if (ratesGraphTimeLine?.length) {
      return ratesGraphTimeLine.length;
    }

    if (period === '364') {
      return 53;
    }

    // fallback for initial render / empty data
    return +period + 1;
  }, [period, ratesGraphTimeLine]);

  const memoizedChangeFilters = useCallback(
    termValue => {
      changeFilters({ ...filters, [termValue]: !filters[termValue] });
    },
    [filters, changeFilters]
  );

  return (
    <>
      <FilterLabel>
        <FormattedMessage id="dynamicRates.terms.label" />
      </FilterLabel>

      <FiltersWrapper>
        <TermsFilters
          terms={preparedTerms}
          filters={filters}
          onRequestChange={memoizedChangeFilters}
        />

        <ButtonGroupWrapper>
          <ButtonGroup
            options={preparedPeriods}
            value={period}
            onSelect={changePeriod}
          />
        </ButtonGroupWrapper>
      </FiltersWrapper>

      <ContainerDimensions>
        {({ width }) => (
          <VirtualizedMultipleLineChart
            periods={ratesGraphTimeLine}
            terms={activeTerms}
            rates={activeRates}
            extent={ratesExtent}
            width={width}
            dotsCount={dotsCount}
            isLoading={isFetching}
            isInitialized={isInitialized}
            isEmpty={ratesMatrix.flat().length === 0}
          />
        )}
      </ContainerDimensions>
    </>
  );
};

export default DynamicRatesChart;
